import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { createTranslationMessages } from '@backstage/frontend-plugin-api';
import {
  SwappableComponentBlueprint,
  TranslationBlueprint,
} from '@backstage/plugin-app-react';
import { scaffolderTranslationRef } from '@backstage/plugin-scaffolder/alpha';
import {
  FormFieldBlueprint,
  TemplateCard,
  createFormField,
} from '@backstage/plugin-scaffolder-react/alpha';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import { jiraBoardsFieldSchema, jiraProjectFieldSchema } from './jiraForm';
import { PandaPreflightField } from './pandaPreflight/PandaPreflightField';
import { TemporalWorkerPreflightField } from './temporalWorkerPreflight/TemporalWorkerPreflightField';
import { JiraInstanceField } from './jiraInstance/JiraInstanceField';
import { JiraBoardsField } from './jiraBoards/JiraBoardsField';
import { JiraProjectField } from './jiraProject/JiraProjectField';
import { ProductTemplateCard } from './ProductTemplateCard';

const scaffolderTranslations = TranslationBlueprint.make({
  name: 'scaffolder-overrides',
  params: {
    resource: createTranslationMessages({
      ref: scaffolderTranslationRef,
      messages: {
        'templateListPage.title': 'Self Services',
        'templateListPage.pageTitle': 'Self Services',
        'templateListPage.templateGroups.defaultTitle': 'Services',
      },
    }),
  },
});

// PandaPreflightCheck: custom scaffolder field that verifies the GitHub App
// and LLM Hub credentials the user just entered, by replaying the exact auth
// chains PANDA uses at runtime. The field only "validates" (via a pattern on
// its return value) once the check has returned ok, which naturally blocks
// the wizard from moving to the Review step until every check passes.
const pandaPreflightFieldSchema = makeFieldSchema({
  output: z => z.string().regex(/^verified$/),
  uiOptions: z => z.object({}).optional(),
});

const pandaPreflightField = FormFieldBlueprint.make({
  name: 'PandaPreflightCheck',
  params: {
    field: async () =>
      createFormField({
        name: 'PandaPreflightCheck',
        component: PandaPreflightField,
        schema: pandaPreflightFieldSchema,
      }),
  },
});

// TemporalWorkerPreflightCheck: verifies every worker object id resolves to a
// real Entra service principal before the wizard can advance. Reads the ids
// from the sibling `workerObjectIds` field via formContext.
const temporalWorkerPreflightFieldSchema = makeFieldSchema({
  output: z => z.string().regex(/^verified$/),
  uiOptions: z => z.object({ workersField: z.string().optional() }).optional(),
});

const temporalWorkerPreflightField = FormFieldBlueprint.make({
  name: 'TemporalWorkerPreflightCheck',
  params: {
    field: async () =>
      createFormField({
        name: 'TemporalWorkerPreflightCheck',
        component: TemporalWorkerPreflightField,
        schema: temporalWorkerPreflightFieldSchema,
      }),
  },
});

// JiraInstancePicker: dropdown (SaaS / On-Premise) that fills two editable URL
// fields — the browse base (ticket links) and the api base (JIRA_BASE_URL for
// the agent runtime). Its value is an object consumed by the template's
// fetch-catalog / fetch-fleet-config steps.
const jiraInstanceFieldSchema = makeFieldSchema({
  output: z =>
    z.object({
      instance: z.string(),
      apiBaseUrl: z.string().url(),
      browseBaseUrl: z.string().url(),
    }),
  uiOptions: z => z.object({}).optional(),
});

const jiraInstanceField = FormFieldBlueprint.make({
  name: 'JiraInstancePicker',
  params: {
    field: async () =>
      createFormField({
        name: 'JiraInstancePicker',
        component: JiraInstanceField,
        schema: jiraInstanceFieldSchema,
      }),
  },
});

const jiraProjectField = FormFieldBlueprint.make({
  name: 'JiraProjectPicker',
  params: {
    field: async () =>
      createFormField({
        name: 'JiraProjectPicker',
        component: JiraProjectField,
        schema: jiraProjectFieldSchema,
      }),
  },
});

const jiraBoardsField = FormFieldBlueprint.make({
  name: 'JiraBoardPicker',
  params: {
    field: async () =>
      createFormField({
        name: 'JiraBoardPicker',
        component: JiraBoardsField,
        schema: jiraBoardsFieldSchema,
      }),
  },
});

// Swaps the scaffolder template card for a rebuilt one that shows a leading
// product mark on templates declaring it. No default to delegate to, so the card
// is reconstructed rather than wrapped (rendering TemplateCard here recurses).
const productTemplateCard = SwappableComponentBlueprint.make({
  name: 'product-template-card',
  params: defineParams =>
    defineParams({
      component: TemplateCard,
      loader: () => ProductTemplateCard,
    }),
});

export const scaffolderModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    scaffolderTranslations,
    pandaPreflightField,
    temporalWorkerPreflightField,
    jiraInstanceField,
    jiraProjectField,
    jiraBoardsField,
    productTemplateCard,
  ],
});
